import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import FormField from "@components/Form/FormFields/FormFields";
import {
  FormFieldConfig,
  ValidationProps,
} from "@components/Form/FormFields/FormFields.interfaces";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";

import Grid from "@components/Grid/Grid";
import SubmitButton from "@components/SubmitButton/SubmitButton";
import { getCountries, getCountriesIso } from "../../services/forms";

import FormErrorMessage from "@components/FormErrorMessage/FormErrorMessage";
import SelectAutocomplete from "@components/SelectAutocomplete/SelectAutocomplete";
import { getBusinessUnit } from "@utilities/getBusinessUnit";
import {
  getCountryCode,
  getInvoiceType,
  getPaymentErrors,
  hasCountryMatch,
} from "@utilities/payment";
import setGADataLayer from "@utilities/setGADataLayer";
import styles from "./DeccPayment.module.scss";

export interface DeccFormDetailsProps {
  emailLabelText: string;
  firstNameLabelText: string;
  lastNameLabelText: string;
  invoiceNumberLabelText: string;
  currencyLabelText: string;
  addressLabelText: string;
  amountLabelText: string;
  companyNameLabelText: string;
  cityLabelText: string;
  postcodeLabelText: string;
  countyLabelText: string;
  mobileNumberLabelText: string;
  submitButtonLabelText: string;
  formDescription: string;
  pageHeaderTitle: string;
  emailHintText: string;
  invoiceNumberHintText: string;
  amountHintText: string;
  studentIdLabelText: string;
  studentIdHintText: string;
  customerIdLabelText: string;
  customerIdHintText: string;
  programmeCodeLabelText: string;
  programmeCodeHintText: string;
  invoiceTypeLabelText: string;
}

export interface CountriesIsoProps {
  countryName: string;
  countryAlpha2Code?: string;
}

let firstInteract = 0;

export const DeccPayment = (props: { content: DeccFormDetailsProps }) => {
  const { content } = props;
  const {
    pageHeaderTitle,
    emailLabelText,
    firstNameLabelText,
    lastNameLabelText,
    invoiceNumberLabelText,
    currencyLabelText,
    addressLabelText,
    companyNameLabelText,
    cityLabelText,
    postcodeLabelText,
    countyLabelText,
    mobileNumberLabelText,
    submitButtonLabelText,
    formDescription,
    emailHintText,
    invoiceNumberHintText,
    amountHintText,
    amountLabelText,
    studentIdLabelText,
    studentIdHintText,
    customerIdLabelText,
    customerIdHintText,
    programmeCodeLabelText,
    programmeCodeHintText,
    invoiceTypeLabelText,
  } = content;

  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [countries, setCountries] = useState([]);
  const [countriesIso, setCountriesIso] = useState<CountriesIsoProps[]>([]);

  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    clearErrors,
    trigger,
  } = useForm({
    mode: "onBlur",
  });

  const invoiceTypeOptions = [
    {
      id: "COMMIT",
      value: "Commitment Fee",
    },
    {
      id: "GE",
      value: "Global Experience",
    },
    {
      id: "PROGRAM",
      value: "Programme Fee",
    },
    {
      id: "RECONFIR",
      value: "Reconfirmation Fee",
    },
    {
      id: "RESERVE",
      value: "Reservation Fee",
    },
    {
      id: "SA FEE",
      value: "Student Association Fee",
    },
    {
      id: "T4",
      value: "Term 4",
    },
  ];

  const { query } = useRouter();

  // Make query parameter checks case-insensitive
  useEffect(() => {
    if (!query) return;

    // Create a map with lowercased keys for case-insensitive access
    const lowerCaseQuery: ParsedUrlQuery = Object.keys(query).reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = query[key];
        return acc;
      },
      {}
    );

    if (lowerCaseQuery.invoicenumber) {
      setValue("invoiceNumber", lowerCaseQuery.invoicenumber);
    }
    if (lowerCaseQuery.invoicetype) {
      const invoiceType = invoiceTypeOptions.find(
        (option) => option.id === lowerCaseQuery.invoicetype
      );
      setValue("invoiceType", invoiceType.value);
    }
    if (lowerCaseQuery.studentnumber) {
      setValue("studentId", lowerCaseQuery.studentnumber);
    }
    if (lowerCaseQuery.programmecode) {
      setValue("programmeCode", lowerCaseQuery.programmecode);
    }
    if (lowerCaseQuery.ledgercode) {
      setValue("ledgerCode", lowerCaseQuery.ledgercode);
    }
    if (lowerCaseQuery.customerid) {
      setValue("customerId", lowerCaseQuery.customerid);
    }
  }, [query, setValue]);

  useEffect(() => {
    const getCountryIsos = async () => {
      const countries = await getCountriesIso();
      setCountriesIso(countries);
    };
    getCountryIsos();
  }, []);

  const sendGAEvent = (event) => {
    setGADataLayer({
      event,
      formName: "DECC Payment",
      programmeCode: "",
      businessUnit: getBusinessUnit(),
    });
  };

  const fetchCountryData = async (term: string) => {
    try {
      if (!term) {
        setCountries([]);
        return;
      }
      const countriesResponse = await getCountries(term);
      setCountries(countriesResponse);
    } catch (_e) {
      setErrors(["Fetching countries data is failed"]);
      setCountries([]);
    }
  };

  useEffect(() => {
    const subscription = watch((_e, a) => {
      if (a.type === "change" && firstInteract < 1) {
        setGADataLayer({
          event: "formStart",
          formName: "DECC Payment",
        });
        firstInteract++;
        return () => subscription.unsubscribe();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const formFields: FormFieldConfig[] = [
    {
      propertyName: "emailAddress",
      validation: {
        isRequired: true,
        email: true,
        maxLength: 255,
      },
      labelText: emailLabelText,
      hintText: emailHintText,
    },
    {
      propertyName: "studentId",
      validation: {
        maxLength: 30,
        alphaNumeric: true,
        eitherOrRequired: {
          fieldName: "customerId",
          fieldLabel: customerIdLabelText,
        },
      },
      labelText: studentIdLabelText,
      hintText: studentIdHintText,
      optionalText: false,
    },
    {
      propertyName: "programmeCode",
      validation: {
        isRequired: true,
        maxLength: 30,
        alphaNumeric: true,
      },
      labelText: programmeCodeLabelText,
      hintText: programmeCodeHintText,
    },
    {
      propertyName: "ledgerCode",
      type: "hidden",
    },
    {
      propertyName: "invoiceType",
      formType: "select",
      type: "select",
      options: invoiceTypeOptions,
      validation: {
        eitherOrRequired: {
          fieldName: "invoiceNumber",
          fieldLabel: invoiceNumberLabelText,
        },
      },
      optionalText: false,
      labelText: invoiceTypeLabelText,
    },
    {
      propertyName: "invoiceNumber",
      validation: {
        maxLength: 30,
        alphaNumeric: true,
        eitherOrRequired: {
          fieldName: "invoiceType",
          fieldLabel: invoiceTypeLabelText,
        },
      },
      labelText: invoiceNumberLabelText,
      hintText: invoiceNumberHintText,
      optionalText: false,
    },
    {
      propertyName: "customerId",
      validation: {
        maxLength: 30,
        alphaNumeric: true,
        eitherOrRequired: {
          fieldName: "studentId",
          fieldLabel: studentIdLabelText,
        },
      },
      labelText: customerIdLabelText,
      hintText: customerIdHintText,
      optionalText: false,
    },
    {
      propertyName: "currencyCode",
      formType: "select",
      options: [
        { id: "GBP", value: "GBP" },
        { id: "USD", value: "USD" },
      ],
      validation: {
        isRequired: true,
      },
      labelText: currencyLabelText,
    },
    {
      propertyName: "payAmount",
      validation: {
        isRequired: true,
        range: { min: 10, max: 120000 },
        decimalValidator: true,
      },
      labelText: amountLabelText,
      hintText: amountHintText,
    },
    {
      propertyName: "firstName",
      validation: {
        isRequired: true,
        maxLength: 30,
        firstName: true,
        alphaNumeric: true,
      },
      labelText: firstNameLabelText,
    },
    {
      propertyName: "lastName",
      validation: {
        isRequired: true,
        maxLength: 30,
        lastName: true,
      },
      labelText: lastNameLabelText,
    },
    {
      propertyName: "companyName",
      validation: {
        maxLength: 30,
      },
      labelText: companyNameLabelText,
    },
    {
      propertyName: "addressLine1",
      validation: {
        isRequired: true,
        maxLength: 30,
      },
      labelText: addressLabelText,
    },
    {
      propertyName: "city",
      validation: {
        isRequired: true,
        maxLength: 30,
        alphaNumeric: true,
      },
      labelText: cityLabelText,
    },
    {
      propertyName: "country",
      fieldType: "selectAutocomplete",
      validation: {
        required: "Country of residence is required",
        maxLength: 30,
        validate: (value) =>
          !!hasCountryMatch(value, countries) || "Unknown country entered",
      },
      labelText: countyLabelText,
    },
    {
      propertyName: "postCode",
      validation: {
        isRequired: true,
        maxLength: 30,
      },
      labelText: postcodeLabelText,
    },
    {
      propertyName: "phoneNumber",
      validation: {
        isRequired: true,
        maxLength: 30,
        telephone: true,
      },
      labelText: mobileNumberLabelText,
    },
  ];

  const onSubmit = async (formData) => {
    setErrors([]);
    setLoading(true);

    const data = {
      ...formData,
      internalReference: "DECC",
      country: getCountryCode(formData.country, countriesIso),
      invoiceType: getInvoiceType(formData.invoiceType, invoiceTypeOptions),
    };

    localStorage.setItem("paymentFormData", JSON.stringify(data));

    const response = await fetch("/api/postPayment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    const postPaymentResponseData = await response.json();

    setLoading(false);

    if (!response.ok) {
      const paymentErrors = postPaymentResponseData?.response
        ? getPaymentErrors(postPaymentResponseData.response)
        : ["Payment request failed"];

      setErrors(paymentErrors);
      window.scrollTo(0, 0);
    }

    if (response.ok) {
      sendGAEvent("formComplete");
      window.location.href = postPaymentResponseData?.response?.checkOutUrl;
    }
  };

  return (
    <div className={clsx(styles["decc-form"], "wrapper")}>
      <Grid row>
        <Grid column sm={12} md={6}>
          <div className={clsx(styles.component, "component", styles.form)}>
            <h1 className="h2">{pageHeaderTitle}</h1>
            <p>{formDescription}</p>
          </div>
          <section
            className={clsx(
              styles.component,
              styles["form"],
              "form",
              styles.cf
            )}
          >
            {errors &&
              errors.map((error, index) => (
                <FormErrorMessage key={index} text={error} />
              ))}
            <form
              method="POST"
              className="form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <section className={styles["form-group-wrapper"]}>
                <div className={styles["form-row"]} />
                {formFields.map((field, index) => {
                  if (field.fieldType === "selectAutocomplete") {
                    return (
                      <SelectAutocomplete
                        key={`${field.labelText}${index}`}
                        placeholder={field.labelText}
                        name={field.propertyName}
                        register={register}
                        trigger={trigger}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        errors={formState.errors}
                        value={watch(field.propertyName)}
                        getOptions={(term) => fetchCountryData(term)}
                        options={countries}
                        validation={{
                          ...field.validation,
                          onBlur: () => {
                            trigger(field.propertyName);
                          },
                        }}
                      />
                    );
                  }

                  return (
                    <FormField
                      key={`${field.labelText}${index}`}
                      formType={field?.formType}
                      type={field?.type}
                      watch={watch}
                      validation={field?.validation as ValidationProps}
                      isEmptyFirstOption
                      register={register}
                      trigger={trigger}
                      property={field.dataText}
                      hintText={field?.hintText}
                      placeholder={field.labelText}
                      options={field.options}
                      optionalText={field?.optionalText}
                      name={field?.propertyName}
                      errors={formState.errors}
                    />
                  );
                })}
              </section>
              <div className="btn-and-text-wrapper">
                <SubmitButton
                  loading={loading}
                  id="submitButton"
                  text={submitButtonLabelText || "Submit"}
                />
              </div>
            </form>
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default DeccPayment;
